import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../services/api';
import { Button } from './ui';
import {
  Zap, Heart, BookOpen, Smile, Lightbulb, Users, Star, Wind, Brain
} from 'lucide-react';

const ActivityLibrary = ({ onActivitySelect }) => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [message, setMessage] = useState(null);

  const categoryIcons = {
    meditation: Brain,
    exercise: Zap,
    journaling: BookOpen,
    gratitude: Heart,
    art_music: Smile,
    learning: Lightbulb,
    social: Users,
    spiritual: Star,
    self_care: Wind,
    nature: Wind,
    other: Brain
  };

  const categoryColors = {
    meditation: 'bg-purple-100 text-purple-700',
    exercise: 'bg-red-100 text-red-700',
    journaling: 'bg-blue-100 text-blue-700',
    gratitude: 'bg-pink-100 text-pink-700',
    art_music: 'bg-yellow-100 text-yellow-700',
    learning: 'bg-indigo-100 text-indigo-700',
    social: 'bg-green-100 text-green-700',
    spiritual: 'bg-orange-100 text-orange-700',
    self_care: 'bg-teal-100 text-teal-700',
    nature: 'bg-emerald-100 text-emerald-700',
    other: 'bg-gray-100 text-gray-700'
  };

  useEffect(() => {
    loadActivities();
  }, []);

  const filterActivities = useCallback(() => {
    let filtered = activities;

    if (selectedCategory) {
      filtered = filtered.filter(a => a.category === selectedCategory);
    }

    if (selectedDifficulty) {
      filtered = filtered.filter(a => a.difficulty === selectedDifficulty);
    }

    setFilteredActivities(filtered);
  }, [activities, selectedCategory, selectedDifficulty]);

  useEffect(() => {
    filterActivities();
  }, [filterActivities]);

  const loadActivities = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await userService.getActivities();
      setActivities(response.data.data.activities || []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load activities'
      });
    } finally {
      setLoading(false);
    }
  };



  const getCategoryLabel = (category) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const IconComponent = ({ category }) => {
    const Icon = categoryIcons[category] || Brain;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-neutral-600">Loading activities...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div
          className={`rounded-soft border p-3 text-sm ${
            message.type === 'error'
              ? 'border-error-200 bg-error-50 text-error-700'
              : 'border-success-200 bg-success-50 text-success-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Filter Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Filter by Category</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={selectedCategory === null ? 'primary' : 'secondary'}
              onClick={() => setSelectedCategory(null)}
              size="sm"
            >
              All Categories
            </Button>
            {[...new Set(activities.map(a => a.category))].map(category => (
              <Button
                key={category}
                type="button"
                variant={selectedCategory === category ? 'primary' : 'secondary'}
                onClick={() => setSelectedCategory(category)}
                size="sm"
              >
                {getCategoryLabel(category)}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Filter by Difficulty</h3>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={selectedDifficulty === null ? 'primary' : 'secondary'}
              onClick={() => setSelectedDifficulty(null)}
              size="sm"
            >
              All Levels
            </Button>
            {['beginner', 'intermediate', 'advanced'].map(level => (
              <Button
                key={level}
                type="button"
                variant={selectedDifficulty === level ? 'primary' : 'secondary'}
                onClick={() => setSelectedDifficulty(level)}
                size="sm"
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Activities Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredActivities.length === 0 ? (
          <div className="col-span-full text-center py-8 text-neutral-500">
            No activities found matching your filters.
          </div>
        ) : (
          filteredActivities.map(activity => (
            <div
              key={activity._id}
              className="rounded-lg border border-neutral-200 bg-white p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg ${categoryColors[activity.category]}`}>
                  <IconComponent category={activity.category} />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-neutral-900">{activity.name}</h4>
                  <div className="flex gap-2 mt-1">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
                      {getCategoryLabel(activity.category)}
                    </span>
                    {activity.difficulty && (
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-neutral-100 text-neutral-700">
                        {activity.difficulty}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {activity.description && (
                <p className="text-sm text-neutral-600 mb-3">{activity.description}</p>
              )}

              {activity.benefits && activity.benefits.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-neutral-700 mb-1">Benefits:</p>
                  <ul className="text-xs text-neutral-600 space-y-0.5">
                    {activity.benefits.slice(0, 2).map((benefit, idx) => (
                      <li key={idx}>• {benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-neutral-200">
                <span className="text-xs text-neutral-500">
                  {activity.estimatedDuration}min
                </span>
                <Button
                  type="button"
                  onClick={() => onActivitySelect(activity)}
                  size="sm"
                >
                  Log Activity
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLibrary;
